import { useState, useRef, useEffect, useMemo } from 'react';
import { User, LogOut, HelpCircle, Sun, Moon, Loader2, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { LanguageSelector } from './LanguageSelector';
import { removeShiftSession } from '../utils/firebase';
import { getPendingActions } from '../utils/offlineDb';
import { SyncManager } from './SyncManager';
import { HelpDialog } from './HelpDialog';

export function ProfileMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingActions, setPendingActions] = useState<number>(0);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { workerId, workerName, logout } = useAuthStore();
  const { isDark, toggle } = useThemeStore();

  const loadPendingActions = async () => {
    if (workerId) {
      try {
        const actions = await getPendingActions();
        setPendingActions(actions.length);
      } catch (error) {
        console.error('Failed to check pending actions:', error);
      }
    }
  };

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    loadPendingActions();
    const interval = setInterval(loadPendingActions, 5000); // Check every 5 seconds

    return () => {
      clearInterval(interval);
    };
  }, [workerId]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const handleLogout = async () => {
    setLoading(true);
    try {
      if (workerId) {
        await removeShiftSession(workerId);
      }
      await logout();
    } finally {
      setLoading(false);
      setShowLogoutConfirm(false);
    }
  };

  const handleThemeToggle = () => {
    toggle();
    setIsOpen(false);
  };

  const handleFormsDocsClick = () => {
    navigate('/forms-docs');
    setIsOpen(false);
  };

  return (
    <div className="relative flex items-center gap-3" ref={menuRef}>
      <SyncManager 
        isOffline={isOffline} 
        pendingActions={pendingActions} 
        onRefresh={loadPendingActions}
      />
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {workerName}
      </span>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-border transition-colors"
        aria-label="Open profile menu"
      > <User className="h-5 w-5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-light-card dark:bg-dark-card rounded-lg shadow-lg border border-light-border dark:border-dark-border z-50 top-full">
          <div className="p-2">
            <div className="mb-2">
              <LanguageSelector />
            </div>

            <button
              onClick={handleFormsDocsClick}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg flex items-center gap-2"
            >
              <FileText className="h-4 w-4" />
              Forms & Docs
            </button>

            <button
              onClick={() => {
                setIsOpen(false);
                setShowHelp(true);
              }}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg flex items-center gap-2"
            >
              <HelpCircle className="h-4 w-4" />
              Help
            </button>
            
            <button
              onClick={handleThemeToggle}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg flex items-center gap-2"
            >
              {isDark ? (
                <>
                  <Sun className="h-4 w-4" />
                  Light Mode
                </>
              ) : (
                <>
                  <Moon className="h-4 w-4" />
                  Dark Mode
                </>
              )}
            </button>

            <button
              onClick={() => {
                setIsOpen(false);
                setShowLogoutConfirm(true);
              }}
              className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        </div>
      )}

      {showHelp && <HelpDialog onClose={() => setShowHelp(false)} />}

      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-light-card dark:bg-dark-card rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Confirm Logout
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to log out?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                onClick={handleLogout} 
                disabled={loading} 
                className="btn-primary"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Logging out...
                  </>
                ) : (
                  'Log out'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}